// SP-2 WS-7 — Monthly Cycle Cockpit TanStack Query hooks. The cycle read
// registers under monthlyCycleKeys; every statement mutation invalidates the
// loan's cycle prefix, the statement queues (pending-review / correction),
// and loan HEALTH (a statement state change flips the loan's cycle facts the
// Overview feed / tab badges may surface). Toasts + ApiError rendering happen
// in the calling component (useServicing / useLoanFees convention).

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { QueryClient } from "@tanstack/react-query";
import {
  loanStatementsApi,
  type GenerateLoanStatementRequest,
} from "@/api/portfolioAdmin/loanStatementsApi";
import {
  loanStatementsKeys,
  monthlyCycleKeys,
} from "@/hooks/portfolioAdmin/loanStatementsKeys";
import { invalidateLoanHealth } from "@/hooks/portfolioAdmin/invalidation";

export function useMonthlyCycle(loanId: string | undefined, period?: string) {
  return useQuery({
    queryKey: monthlyCycleKeys.cycle(loanId ?? "", period),
    queryFn: () => loanStatementsApi.monthlyCycle(loanId!, period),
    enabled: !!loanId,
  });
}

/** Statement lifecycle changes flip the cockpit's steps AND the cross-loan
 *  statement queues; loan health is the single severity source.
 *
 *  Exported because a statement's content also moves from OUTSIDE this file:
 *  approving a fee flips the open statement's `HasUnappliedChanges`, and
 *  waiving an obligation regenerates every statement that billed it
 *  (ObligationWaiveService -> RegenerateAsync). Those callers reuse this so
 *  "statement scope" keeps ONE definition — a queue key added here reaches
 *  them for free. */
export function invalidateCycleScope(qc: QueryClient, loanId: string) {
  void qc.invalidateQueries({ queryKey: monthlyCycleKeys.loanPrefix(loanId) });
  void qc.invalidateQueries({ queryKey: loanStatementsKeys.statementsPrefix() });
  void invalidateLoanHealth(qc, loanId);
}

export function useGenerateStatement(loanId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: GenerateLoanStatementRequest) =>
      loanStatementsApi.generate(loanId, body),
    onSuccess: () => invalidateCycleScope(qc, loanId),
  });
}

export function useSendStatementToTps(loanId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (statementId: string) => loanStatementsApi.sendToTps(statementId),
    onSuccess: () => invalidateCycleScope(qc, loanId),
  });
}

export function useRegenerateStatement(loanId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (statementId: string) => loanStatementsApi.regenerate(statementId),
    onSuccess: () => invalidateCycleScope(qc, loanId),
  });
}
